import { ChangeEvent, Dispatch, MouseEvent, SetStateAction, useRef, useState } from 'react'
import { AxiosResponse } from 'axios'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import { Dropdown } from 'react-bootstrap'

import { PaymentTicket, TicketActionResponse, TicketListObject } from '../core/_models'
import { finishErrorText } from '../core/ticketErrors'
import { KTIcon } from '../../../../_metronic/helpers'
import { convertUTCtoMSK, formatRuPhoneE164, formatSum } from '../../../../utils'
import {
  finishTicketAPI,
  cancelTicketAPI,
  ticketFileGet,
  ticketAppendMerchantGet,
  setFakeTicketAPI,
  resendCallbackAPI,
} from '../core/_requests'
import {
  FORBIDDEN_TEXT,
  getErrorCode,
  getErrorStatus,
  getErrorText,
  getGeneralError,
  SERVICE_UNAVAILABLE_TEXT,
} from '../../../common/api/errors'
import { ROLE, useAuth } from '../../auth'
import { ETicketStatus } from '../../../../types/enums'
import MerchantFilter from '../../../common/filter/MerchantFilter'
import { IsGranted } from '../../auth/PrivateRoute'
import ConciergeModal from './concierge/ConciergeModal'
import { CONCIERGE_ROLES } from '../core/_conciergeModels'
import { Merchant } from '../../../common/core/_models'
import { RowKebabMenu, RowKebabSlot } from '../../../common/RowKebabMenu'
import { AmountChangeBadge } from '../../../../components/AmountChangeBadge'
import { ClientCell } from '../../../common/ClientCell'
import { DepositTokenLink } from '../../../common/DepositTokenLink'
import { escapeHtml } from '../../../common/escapeHtml'
import { callbackResendErrorText } from '../core/callbackResendErrors'

const AppSwal = withReactContent(Swal)

/**
 * CPAYMENT-1669: сумма к переводу — то, что клиент действительно должен перевести (после
 * кастомизации мэтчинга или выбора варианта). Исходный запрос остаётся только в бейдже.
 */
export const issuedAmountOf = (ticket: {
  externalUserApproximateAmount: number | string | null
  requisiteAmount?: number | null
}): number => Number(ticket.requisiteAmount ?? ticket.externalUserApproximateAmount ?? 0)

/**
 * Текст неудачной отмены. Причину отказа показываем дословно только для тикета виртуального мерчанта —
 * у неё русский текст бэкенда; прочие английские `general_error` сотруднику не показываем.
 */
export const cancelErrorText = (err: unknown): string => {
  const code = getErrorCode(err)
  if (code === 'TICKET_VIRTUAL_MERCHANT') {
    return getGeneralError(err) ?? 'Нельзя отменить тикет, так как он управляется виртуальным мерчантом'
  }
  if (code === 'TICKET_STATE_INVALID') return 'Тикет не может быть отменен'
  const status = getErrorStatus(err)
  if (status === 403) return getErrorText(err, FORBIDDEN_TEXT)
  if (status === 503) return SERVICE_UNAVAILABLE_TEXT

  return 'Не удалось отменить тикет'
}

const ActiveTicketItem = (props: {
  ticket: PaymentTicket
  removeTicketFromList: (token: string) => void
  updateTicketList: () => void
  setShowAlert: Dispatch<SetStateAction<boolean>>
  merchants?: Array<Merchant>
  setTickets: Dispatch<SetStateAction<TicketListObject>>
}) => {
  const amountInputRef = useRef<HTMLInputElement>(null)
  const [amount, setAmount] = useState('')
  const ticket = useState(Object.assign(props.ticket, {}))[0]
  const { currentUser } = useAuth()
  const [showTooltip, setShowTooltip] = useState<boolean>(false)
  const [conciergeOpen, setConciergeOpen] = useState<boolean>(false)
  const canConcierge = IsGranted(CONCIERGE_ROLES, currentUser)
  const canResend = IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR], currentUser)
  // Логи аудита — с ROLE_MANAGER (наследуется ROLE_ADMIN), как у AuditLogLink.
  const canSeeAudit = IsGranted([ROLE.ROLE_MANAGER], currentUser)

  const cancelTicket = async () => {
    AppSwal.fire({
      title: 'Вы точно хотите отменить операцию?',
      showCancelButton: true,
      cancelButtonText: 'Нет',
      confirmButtonText: 'Да, отменить',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const response = await cancelTicketAPI(ticket.token, currentUser)

          if (response.data?.success) {
            handleCancelFinishResponse(response)
            return true
          }

          AppSwal.fire({ title: 'Ошибка', text: cancelErrorText(response), icon: 'error' })
          return false
        } catch (err) {
          AppSwal.fire({ title: 'Ошибка', text: cancelErrorText(err), icon: 'error' })
          return false
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result) => {
      if (result.isConfirmed && result.value === true) {
        AppSwal.fire({
          title: `Операция отменена успешно!`,
          timer: 1500,
        })
      }
    })
  }

  const finishTicket = async (externalUserId: string = '') => {
    try {
      const response = await finishTicketAPI(ticket.token, externalUserId, amount, currentUser)
      handleCancelFinishResponse(response)
    } catch (err) {
      // axios rejects every non-2xx: the reason is read from the rejected response, not from `response.status`.
      Swal.fire({ title: '', text: finishErrorText(err), icon: 'warning' })
    }
  }

  const handleCancelFinishResponse = (response: AxiosResponse<TicketActionResponse>) => {
    if (!response.data) {
      return false
    }

    if (response.data.success && response.data.ticket) {
      if (response.data.ticket.status !== ETicketStatus.ACTIVE) {
        props.removeTicketFromList(ticket.token)
      }
    }
  }

  const onChangeMerchant = async (selected: any) => {
    await ticketAppendMerchantGet(ticket.token, selected.value[0])
    props.updateTicketList()
  }

  const resendCallback = async () => {
    AppSwal.fire({
      title: 'Отправить КБ мерчанту?',
      text: 'Мерчанту будет повторно отправлен колбэк о текущем статусе тикета с актуальной подписью.',
      icon: 'question',
      showCancelButton: true,
      cancelButtonText: 'Отмена',
      confirmButtonText: 'Отправить',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const response = await resendCallbackAPI(ticket.token)
          if (!response.data?.success) {
            throw new Error('сервис вернул ошибку')
          }
        } catch (err) {
          AppSwal.showValidationMessage(escapeHtml(callbackResendErrorText(err)))
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result) => {
      if (result.isConfirmed) {
        AppSwal.fire({
          title: 'КБ отправлен',
          icon: 'success',
          timer: 1500,
        })
      }
    })
  }

  const handleCopyToClipboard = (e: MouseEvent<HTMLSpanElement>) => {
    if (!ticket.lastExternalTransaction.requisite) return
    navigator.clipboard.writeText(formatRuPhoneE164(ticket.lastExternalTransaction.requisite))
    props.setShowAlert(true)
    setTimeout(() => {
      props.setShowAlert(false)
    }, 1400)
  }

  const fakeHandler = async () => {
    const response = await setFakeTicketAPI(ticket.token)
    props.setTickets((prev) => {
      const newTickets = { ...prev }
      newTickets[ticket.token].isFake = response?.data.ticket.isFake
      return newTickets
    })
  }

  return (
    <>
      <tr>
        <td className='w-300px'>
          <p className='mb-4'><DepositTokenLink token={ticket.token} /></p>
          <div className='d-flex-column'>
            <p className='fw-bold fs-9'>ID адаптера:</p>
            <span className='fs-9'>{ticket?.lastExternalTransaction?.paymentId || '-'}</span>
          </div>
        </td>
        <td className='w-100px'>{ticket?.transactionId}</td>
        <td>{convertUTCtoMSK(ticket.createdAt)}</td>
        {IsGranted(
          [
            ROLE.ROLE_ADMIN,
            ROLE.ROLE_MANAGER,
            ROLE.ROLE_CASHIER,
            ROLE.ROLE_SENIOR_CASHIER,
            ROLE.ROLE_SENIOR_OPERATOR,
            ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER,
          ],
          currentUser
        ) && (
          <td>
            {ticket.amount ? (
              <>
                <div>{formatSum(issuedAmountOf(ticket))}</div>
                <div className='fs-9 text-muted'>факт: {formatSum(ticket.amount)}</div>
                <AmountChangeBadge
                  requestedAmount={ticket.externalUserApproximateAmount}
                  issuedAmount={ticket.requisiteAmount ?? null}
                  source={ticket.amountChangeSource ?? null}
                />
              </>
            ) : (
              <>
              <input
                className={`border border-${
                  parseFloat(amount) <= 0 || amount === '' || (!IsGranted([ROLE.ROLE_ADMIN], currentUser) && parseFloat(amount) >= 100000) ? 'danger' : 'success'
                }`}
                ref={amountInputRef}
                type='number'
                min={0.1}
                max={!IsGranted([ROLE.ROLE_ADMIN], currentUser) ? 99999.99 : undefined}
                required
                value={amount}
                // CPAYMENT-1616: пока идёт авто-проверка чека, перехода смены суммы из этого
                // статуса в workflow нет — ввод блокируем, иначе запрос вернёт 422.
                disabled={ticket.status === 'FILES_AUTO_VERIFICATION'}
                title={
                  ticket.status === 'FILES_AUTO_VERIFICATION'
                    ? 'Идёт проверка чека — сумму можно изменить после неё'
                    : undefined
                }
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value
                  // Ограничиваем ввод до 100000 (не включая)
                  if (value === '' || parseFloat(value) < 100000 || IsGranted([ROLE.ROLE_ADMIN], currentUser)) {
                    setAmount(value)
                  }
                }}
                placeholder={`${formatSum(issuedAmountOf(ticket))}`}
                style={{ width: '100px', color: '#000000', padding: '4px' }}
              />
              <AmountChangeBadge
                requestedAmount={ticket.externalUserApproximateAmount}
                issuedAmount={ticket.requisiteAmount ?? null}
                source={ticket.amountChangeSource ?? null}
              />
              </>
            )}
          </td>
        )}
        <td>{ticket?.lastExternalTransaction?.paymentAdapterName}</td>
        <td
          className='cursor-pointer position-relative'
          onClick={handleCopyToClipboard}
          onMouseEnter={() => setShowTooltip((prev) => !prev)}
          onMouseLeave={() => setShowTooltip((prev) => !prev)}
        >
          {showTooltip && ticket?.paymentMethod?.type === 'CRYPTO' && (
            <div
              style={{
                zIndex: 20,
                position: 'absolute',
                top: '5px',
                left: '10px',
              }}
              className='badge bg-success text-white'
            >
              <p>{ticket?.lastExternalTransaction?.requisite || '-'}</p>
            </div>
          )}
          <span>
            {ticket?.paymentMethod?.type === 'CRYPTO'
              ? ticket?.lastExternalTransaction?.requisite?.length > 16
                ? ticket?.lastExternalTransaction?.requisite.slice(0, 15) + '...'
                : ticket?.lastExternalTransaction?.requisite || '-'
              : ticket?.lastExternalTransaction?.requisite ? formatRuPhoneE164(ticket?.lastExternalTransaction?.requisite) : '-'}
          </span>
          {ticket?.paymentMethod?.type !== 'CRYPTO' ? (
            <>
              <span className='separator' />
              <span>{ticket?.lastExternalTransaction?.bank?.toUpperCase() || '-'}</span>
            </>
          ) : (
            <></>
          )}
          {ticket?.paymentMethod?.type === 'CRYPTO' && (
            <>
              <span className='separator' />
              <span>{ticket?.paymentCurrency || '-'}</span>
            </>
          )}
        </td>
        {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_SENIOR_OPERATOR, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
          <td>{ticket?.cashBox?.title || '-'}</td>
        )}
        <ClientCell
          externalId={ticket?.externalUser?.externalId}
          token={ticket?.externalUser?.token}
        />
        <td>{ticket.sms ? ticket.sms.text : 'смс не поступило'}</td>
        {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
          <td>
            {ticket.merchant?.title || (
              <MerchantFilter
                changeSelect={onChangeMerchant}
                value={['']}
                preloadData={props.merchants}
              />
            )}
          </td>
        )}
        {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_SENIOR_OPERATOR, ROLE.ROLE_OPERATOR, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
          <td>{ticket?.lastExternalTransaction?.adapterStatus || '-'}</td>
        )}
        <td>
          <input
            onChange={fakeHandler}
            className='form-check-input'
            type='checkbox'
            value=''
            id='flexCheckDefault'
            checked={ticket?.isFake}
            // disabled={ticket?.isFake}
          />
        </td>
        {canConcierge && (
          <td>
            {ticket.concierge && (
              <button type='button' className='btn btn-sm btn-light-primary' onClick={() => setConciergeOpen(true)}>
                Консьерж
              </button>
            )}
            {conciergeOpen && (
              <ConciergeModal
                token={ticket.token}
                show={conciergeOpen}
                onClose={() => {
                  setConciergeOpen(false)
                  props.updateTicketList()
                }}
              />
            )}
          </td>
        )}
        <td className='text-end'>
          {IsGranted(
            [
              ROLE.ROLE_ADMIN,
              ROLE.ROLE_MANAGER,
              ROLE.ROLE_SENIOR_CASHIER,
              ROLE.ROLE_CASHIER,
              ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER,
            ],
            currentUser
          ) && (
            <>
              {(ticket.source.value === 'MERCHANT' && ticket.status === 'WAITING_FOR_REQUISITE') ||
              ticket.status === ETicketStatus.MANUAL_REQUISITE ||
              (ticket.source.value === 'SMS' && ticket.status === 'WAITING_FOR_MERCHANT') ? null : (
                <button
                  className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1 me-4'
                  onClick={() => {
                    if (ticket.amount || (parseFloat(amount) > 0 && (parseFloat(amount) < 100000 || IsGranted([ROLE.ROLE_ADMIN], currentUser)))) {
                      finishTicket()
                    } else {
                      amountInputRef?.current?.focus()
                    }
                  }}
                >
                  <KTIcon className='fs-1' iconName='check-circle' />
                </button>
              )}
              <button
                className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1 fs-6'
                onClick={() => cancelTicket()}
              >
                <KTIcon className='fs-1' iconName='cross-circle' />
              </button>
            </>
          )}
        </td>
        <td className='text-end'>
          <RowKebabSlot>
            {(canResend || canSeeAudit) && (
              <RowKebabMenu title='Действия по тикету'>
                {/* Повторная отправка КБ: список ролей обязан совпадать с бэкендом — там IsGranted на
                    POST /api/internal/callback/resend плюс правило в security.yaml. Оператору платежей
                    мерчанта бэкенд сужает отправку своими мерчантами. */}
                {canResend && (
                  <Dropdown.Item as='button' type='button' className='d-flex align-items-center' onClick={resendCallback}>
                    <KTIcon iconName='arrows-circle' className='fs-3 me-2' />
                    Отправить КБ
                  </Dropdown.Item>
                )}
                {canSeeAudit && (
                  <Dropdown.Item
                    href={`/logs/audit?ticket_token=${encodeURIComponent(ticket.token)}`}
                    target='_blank'
                    rel='noopener'
                    className='d-flex align-items-center'
                  >
                    <KTIcon iconName='magnifier' className='fs-3 me-2' />
                    Лог аудита
                  </Dropdown.Item>
                )}
              </RowKebabMenu>
            )}
          </RowKebabSlot>
        </td>
      </tr>
    </>
  )
}

export default ActiveTicketItem
